import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from '@phosphor-icons/react';

import { cn } from '../../utils/cn';
import pt from '../../locales/pt';
import {
  computeTotalTarget,
  formatProtectedMonthsLabel,
  parseAmount,
  progressSegments,
  protectedMonthsFromSaved,
} from '../../lib/emergencyFundDisplayUtils';
import { formatEUR } from '../../utils/money';

export function EmergencyFundDoneStep({
  avgMonthly,
  months,
  savedTotal,
  suggestedMonthly,
  monthsHorizon,
}) {
  const copy = pt.emergencyFund.wizard.done;
  const target = computeTotalTarget(avgMonthly, months);
  const saved = parseAmount(savedTotal);
  const gap = Math.max(0, target - saved);
  const monthly = parseAmount(suggestedMonthly);
  const protectedN = protectedMonthsFromSaved(saved, avgMonthly);
  const protectedLabel = formatProtectedMonthsLabel(protectedN, copy);
  const segs = progressSegments(saved, target, 10);
  const eta = Math.max(1, Number(monthsHorizon) || months || 1);

  const suggestionValue =
    monthly > 0 && gap > 0
      ? copy.suggestionValue.replace('{monthly}', formatEUR(monthly))
      : copy.suggestionComplete;

  return (
    <div className="flex flex-col items-center gap-4 pt-2 text-center">
      <motion.div
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 18 }}
        className="flex h-[76px] w-[76px] items-center justify-center rounded-full bg-sage-600 shadow-[0_14px_30px_-12px_rgba(95,126,84,0.7)]"
      >
        <ShieldCheck className="h-[38px] w-[38px] text-white" weight="regular" aria-hidden />
      </motion.div>

      <div>
        <h1 className="font-display text-[25px] font-semibold leading-tight tracking-tight text-foreground">
          {copy.title.replace('{protected}', protectedLabel)}
        </h1>
        <p className="mx-auto mt-2 max-w-[290px] text-sm leading-relaxed text-muted-foreground">
          {copy.hint}
        </p>
      </div>

      <div className="w-full">
        <div className="flex gap-0.5" aria-hidden>
          {segs.map((on, i) => (
            <span
              key={i}
              className={cn(
                'h-[9px] flex-1 rounded-[3px]',
                on ? 'bg-sage-600' : 'bg-sage-100 dark:bg-sage-900/50',
              )}
            />
          ))}
        </div>
        <div className="mt-2 flex justify-between text-xs">
          <span className="font-bold tabular-nums text-sage-600 dark:text-sage-400">
            {formatEUR(saved)}
          </span>
          <span className="tabular-nums text-muted-foreground">
            {copy.metaLabel.replace('{target}', formatEUR(target))}
          </span>
        </div>
      </div>

      <div className="w-full overflow-hidden rounded-2xl border border-border text-left">
        <div className="flex items-center justify-between px-[15px] py-3.5">
          <span className="text-[13px] text-muted-foreground">{copy.rows.gap}</span>
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {formatEUR(gap)}
          </span>
        </div>
        <div className="flex items-center justify-between border-t border-border/80 px-[15px] py-3.5">
          <span className="text-[13px] text-muted-foreground">{copy.rows.suggestion}</span>
          <span className="text-sm font-semibold tabular-nums text-sage-600 dark:text-sage-400">
            {suggestionValue}
          </span>
        </div>
      </div>

      <div className="flex w-full items-start gap-[11px] rounded-[14px] border border-sage-200 bg-sage-50 px-[15px] py-3.5 text-left dark:border-sage-800 dark:bg-sage-950/40">
        <ArrowRight
          className="mt-0.5 h-[18px] w-[18px] shrink-0 text-sage-600 dark:text-sage-400"
          weight="regular"
          aria-hidden
        />
        <p className="m-0 text-[12.5px] leading-relaxed text-sage-800 dark:text-sage-200/90">
          {copy.etaTip.replace('{months}', String(eta))}
        </p>
      </div>
    </div>
  );
}

export default EmergencyFundDoneStep;
